import type { CacheId } from "./rangeCache";
import { rangeCache } from "./rangeCache";
import { Semaphore, retry } from "../util/async";
import { formatBytes } from "../util/format";

/** How geotiff.js should be pointed at the underlying bytes. */
export type GeotiffTarget = { kind: "url"; url: string } | { kind: "blob"; blob: Blob };

/**
 * Random-access byte source. Both the GeoTIFF reader and the remote-zip reader work by
 * pulling small ranges out of very large files, so everything downstream is written
 * against this interface instead of assuming a download.
 */
export interface RangeSource {
  /** Short name for status messages. */
  readonly label: string;
  /** Total byte length. */
  size(): Promise<number>;
  read(offset: number, length: number, signal?: AbortSignal): Promise<Uint8Array>;
  geotiffTarget(): Promise<GeotiffTarget>;
  /** Bytes actually transferred so far, for the UI. */
  readonly bytesRead: number;
  dispose(): void;
}

export class RangeNotSupportedError extends Error {
  constructor(label: string) {
    super(
      `${label}: the server ignored the Range request and returned the whole file. ` +
        `This viewer needs HTTP range requests to stream large datasets.`,
    );
    this.name = "RangeNotSupportedError";
  }
}

/**
 * Reads byte ranges over HTTP.
 *
 * The URL is produced by a callback rather than passed in, because Trimble Connect hands
 * out short-lived pre-signed download URLs: when one expires mid-session the source
 * transparently asks for a fresh one and retries.
 */
export class HttpRangeSource implements RangeSource {
  private cachedUrl: string | null = null;
  private urlPromise: Promise<string> | null = null;
  private totalSize: number | null = null;
  private readonly gate: Semaphore;
  bytesRead = 0;

  /**
   * Identity used to persist ranges across sessions, independent of the pre-signed URL
   * (which is re-signed, and would otherwise change on every request). Absent for sources
   * with no stable identity to cache against.
   */
  private readonly cache?: CacheId;

  constructor(
    readonly label: string,
    private readonly urlProvider: () => Promise<string>,
    options: { concurrency?: number; cache?: CacheId } = {},
  ) {
    this.gate = new Semaphore(options.concurrency ?? 6);
    this.cache = options.cache;
    // Drop any bytes left over from a previous version of this same file straight away,
    // rather than waiting for them to age out under LRU eviction.
    if (this.cache) rangeCache.pruneGroupExcept(this.cache.group, this.cache.key);
  }

  private async url(force = false): Promise<string> {
    if (force) {
      this.cachedUrl = null;
      this.urlPromise = null;
    }
    if (this.cachedUrl) return this.cachedUrl;
    if (!this.urlPromise) {
      this.urlPromise = this.urlProvider().then((url) => {
        this.cachedUrl = url;
        return url;
      });
      // Let a failed lookup be retried rather than caching the rejection forever.
      this.urlPromise.catch(() => {
        this.urlPromise = null;
      });
    }
    return this.urlPromise;
  }

  async size(): Promise<number> {
    if (this.totalSize !== null) return this.totalSize;
    if (this.cache) {
      const cached = await rangeCache.getSize(this.cache.key);
      if (cached !== undefined) {
        this.totalSize = cached;
        return this.totalSize;
      }
    }
    this.totalSize = await this.fetchSize();
    if (this.cache) rangeCache.putSize(this.cache.key, this.totalSize);
    return this.totalSize;
  }

  private async fetchSize(): Promise<number> {
    // A one-byte ranged GET works on pre-signed URLs where HEAD is often not allowed by the
    // CORS policy, and normally reports the total length via Content-Range on the resulting
    // 206. But Content-Range is not a CORS-safelisted header, so unless the server lists it
    // in Access-Control-Expose-Headers, `response.headers.get("content-range")` silently
    // comes back null on a cross-origin fetch — which is exactly what Trimble Connect's
    // pre-signed download URLs do. Falling back to that same response's Content-Length would
    // then report the length of the single requested byte, not the file: correct only when
    // the range was ignored outright and the whole file came back (status 200). When neither
    // holds, size() falls back to an unranged GET below — Content-Length *is* safelisted, so
    // it's readable there — with the body cancelled the moment headers arrive so nothing
    // beyond that is actually transferred.
    const response = await this.fetchRange(0, 1);
    const contentRange = response.headers.get("content-range");
    const match = contentRange?.match(/\/\s*(\d+)\s*$/);
    if (match) {
      await response.body?.cancel().catch(() => undefined);
      return Number(match[1]);
    }
    if (response.status === 200) {
      const length = response.headers.get("content-length");
      await response.body?.cancel().catch(() => undefined);
      if (!length) throw new Error(`${this.label}: server did not report a file size`);
      return Number(length);
    }
    await response.body?.cancel().catch(() => undefined);

    return this.gate.run(() =>
      retry(async (attempt) => {
        const url = await this.url(attempt > 0);
        const whole = await fetch(url, { credentials: "omit", mode: "cors" });
        if (!whole.ok) throw new Error(`${this.label}: HTTP ${whole.status} reading file size`);
        const length = whole.headers.get("content-length");
        await whole.body?.cancel().catch(() => undefined);
        if (!length) throw new Error(`${this.label}: server did not report a file size`);
        return Number(length);
      }, { attempts: 4 }),
    );
  }

  async read(offset: number, length: number, signal?: AbortSignal): Promise<Uint8Array> {
    if (length <= 0) return new Uint8Array(0);
    if (this.cache) {
      const cached = await rangeCache.getRange(this.cache.key, offset, length);
      if (cached) return cached;
    }
    const response = await this.fetchRange(offset, length, signal);
    const buffer = await response.arrayBuffer();
    if (response.status === 200 && buffer.byteLength > length) {
      // A 200 means the range header was ignored: the caller asked for a slice of a
      // multi-gigabyte file and would otherwise silently download all of it.
      throw new RangeNotSupportedError(this.label);
    }
    this.bytesRead += buffer.byteLength;
    const bytes = new Uint8Array(buffer);
    if (this.cache) rangeCache.putRange(this.cache.key, this.cache.group, offset, length, bytes);
    return bytes;
  }

  private async fetchRange(offset: number, length: number, signal?: AbortSignal): Promise<Response> {
    const end = offset + length - 1;
    return this.gate.run(() =>
      retry(
        async (attempt) => {
          const url = await this.url(attempt > 0);
          const response = await fetch(url, {
            headers: { Range: `bytes=${offset}-${end}` },
            signal,
            credentials: "omit",
            mode: "cors",
          });
          if (!response.ok) {
            throw new Error(
              `${this.label}: HTTP ${response.status} reading bytes ${offset}-${end} ` +
                `(${formatBytes(length)})`,
            );
          }
          return response;
        },
        {
          attempts: 4,
          signal,
          // 403/401 usually means the pre-signed URL expired: retrying re-signs it.
          shouldRetry: (error) => !(error instanceof RangeNotSupportedError),
        },
      ),
    );
  }

  async geotiffTarget(): Promise<GeotiffTarget> {
    return { kind: "url", url: await this.url() };
  }

  dispose(): void {
    this.cachedUrl = null;
    this.urlPromise = null;
  }
}

/** Random access over a local `File` picked from disk or dropped onto the viewer. */
export class BlobRangeSource implements RangeSource {
  bytesRead = 0;

  constructor(
    readonly label: string,
    private readonly blob: Blob,
  ) {}

  async size(): Promise<number> {
    return this.blob.size;
  }

  async read(offset: number, length: number): Promise<Uint8Array> {
    const slice = this.blob.slice(offset, offset + length);
    const buffer = await slice.arrayBuffer();
    this.bytesRead += buffer.byteLength;
    return new Uint8Array(buffer);
  }

  async geotiffTarget(): Promise<GeotiffTarget> {
    return { kind: "blob", blob: this.blob };
  }

  dispose(): void {}
}
